/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, OnDestroy } from '@angular/core';
import { DialogService } from './dialog.service';
import { Store, select } from '@ngrx/store';
import { UserState } from 'src/app/state/user-state';
import { environment } from 'src/environments/environment';
import * as signalR from '@microsoft/signalr';
import { selectClientId, selectUserInfo } from 'src/app/state/reducer/user.reducer';
import { NotificationResponse } from '../models/responses/notificationResponse';
import * as UserActions from 'src/app/state/actions/user.actions';
import { Router } from '@angular/router';
import { ROUTES } from '../constants/routes';
import { CONNECTION_EVENTS } from '../constants/enums';
import { Subject, takeUntil } from 'rxjs';

export interface IHubConnection {
  connection: signalR.HubConnection | null;
  url: string;
  updateMBC: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationHubService implements OnDestroy {
  clientId: string | undefined;
  hubConnections: IHubConnection[] = [];
  browserId: string | undefined;
  isDisconnecting: boolean = false;
  userInfo: any;
  ngUnsubscribe = new Subject<void>();

  constructor(
    private store: Store<UserState>,
    private dialogService: DialogService,
    private router: Router
  ) {
    this.hubConnections = [
      { connection: null, url: environment.loyaltyHubUrl, updateMBC: true },
      { connection: null, url: environment.spendingHubUrl, updateMBC: true },
      {
        connection: null,
        url: environment.cateringPurchaseHubUrl,
        updateMBC: true
      },
      {
        connection: null,
        url: environment.bettingPurchaseHubUrl,
        updateMBC: false
      },
      {
        connection: null,
        url: environment.bettingSpendingHubUrl,
        updateMBC: true
      },
      {
        connection: null,
        url: environment.notificationHubUrl,
        updateMBC: true
      }
    ];
  }

  connectToSignalR() {
    this.store
      .pipe(takeUntil(this.ngUnsubscribe), select(selectClientId))
      .subscribe((client_ID: string) => {
        this.clientId = client_ID;
        if (this.clientId === null) {
          return;
        }
        this.browserId = localStorage.getItem('browserId')!;
        if (!this.browserId || this.browserId === undefined) {
          this.browserId = crypto.randomUUID();
          localStorage.setItem('browserId', this.browserId!);
        }
        this.hubConnections.forEach((hubConnection: any) => {
          this.establishHubConnection(this.clientId!, hubConnection, this.browserId!);
        });
      });
    this.store
      .pipe(takeUntil(this.ngUnsubscribe), select(selectUserInfo))
      .subscribe((userInfo: any) => {
        this.userInfo = userInfo;
      });
  }

  establishHubConnection(clientId: string, hubConnection: any, browserId: string) {
    hubConnection.connection = SignalRConnection.getLoyaltyConnectionInstance(
      clientId!,
      browserId,
      hubConnection.connection!,
      hubConnection.url
    );
    hubConnection.connection.onclose(() => {
      if (hubConnection.connection?.state === CONNECTION_EVENTS.CONNECTED) {
        this.disconnectFromSignalR();
      }
    });
    if (
      hubConnection.connection &&
      hubConnection.connection.state === CONNECTION_EVENTS.DISCONNECTED
    ) {
      hubConnection
        .connection!.start()
        .then()
        .catch(function (err: Error) {
          return console.error(err.toString());
        });

      hubConnection.connection!.on(CONNECTION_EVENTS.RECIEVE_MESSAGE, (message: string) => {
        const notification: NotificationResponse | any = JSON.parse(message);
        selectUserInfo;
        if (hubConnection.url === environment.notificationHubUrl && this.userInfo) {
          this.store.dispatch(
            UserActions.UPDATE_POINTS_COUNT({ pointsCount: notification.pointsCount })
          );
          this.store.dispatch(
            UserActions.SET_UNREAD_NOTIFICATIONS_COUNT({
              unreadNotificationCount: notification.unreadNotificationCount
            })
          );
          return;
        }
        if (notification.NotificationType == '1') {
          this.dialogService.openError(notification.MessageTitle, notification.MessageDescription);
        } else {
          if (hubConnection.updateMBC && notification.MBC) {
            const newPointsCount = Number(notification.MBC);
            this.store.dispatch(UserActions.UPDATE_POINTS_COUNT({ pointsCount: newPointsCount }));
          }
          if (notification.MessageDescription.includes('Uspešno kupljen MaxLoyalty vaučer')) {
            this.dialogService.openVoucherDialog(
              notification.MessageTitle,
              notification.MessageDescription,
              true
            );
          } else {
            this.dialogService.openConfirmation(
              notification.MessageTitle,
              notification.MessageDescription,
              hubConnection.updateMBC
            );
          }
        }
      });
      hubConnection.connection!.on(CONNECTION_EVENTS.DISCONNECT, (browserId: string) => {
        if (
          hubConnection.connection?.state === CONNECTION_EVENTS.CONNECTED &&
          this.browserId !== browserId
        ) {
          this.disconnectFromSignalR();
        }
      });
    }
  }

  async disconnectFromSignalR(): Promise<void> {
    if (this.isDisconnecting) {
      return;
    }
    this.isDisconnecting = true;

    const allDisconnected = this.hubConnections.every(
      (hubConnection) =>
        !hubConnection.connection ||
        hubConnection.connection.state === CONNECTION_EVENTS.DISCONNECTED
    );
    if (allDisconnected) {
      this.isDisconnecting = false;
      localStorage.clear();
      this.router.navigate([ROUTES.LOGIN]);
      return;
    }
    localStorage.clear();

    const disconnectPromises = this.hubConnections.map(async (hubConnection) => {
      if (hubConnection.connection?.state === CONNECTION_EVENTS.CONNECTED) {
        await hubConnection.connection.stop();
      }
      hubConnection.connection = null;
    });

    await Promise.all(disconnectPromises);

    this.dialogService.closeDialog();
    this.router.navigate([ROUTES.LOGIN]);
    this.isDisconnecting = false;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}

class SignalRConnection {
  private constructor() {
    // Private constructor to prevent instantiation.
  }

  public static getLoyaltyConnectionInstance(
    clientId: string,
    browserId: string,
    connection: signalR.HubConnection,
    baseUrl: string
  ): signalR.HubConnection {
    if (!connection) {
      const url = new URL(baseUrl);
      url.searchParams.set('userid', clientId);
      url.searchParams.set('browserid', browserId);

      connection = new signalR.HubConnectionBuilder()
        .withUrl(url.toString(), {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets
        })
        .configureLogging(signalR.LogLevel.None)
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .build();
    }
    return connection;
  }
}
